// src/state/otherUsersStore.js
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
/**
 * A global, normalized cache for "other users" (not the signed-in user).
 * - entities.users[u_id] => { u_id, first_name, last_name, full_name, avatar, b_id }
 * - entities.businesses[b_id] => { b_id, b_name, b_industry }
 * - ratings[u_id] => { avg, count }
 *
 * You can seed from any existing list (e.g., TeamInfo nearbyUsers) with seedFromArray(),
 * or fetch missing profiles by ID with fetchByIds().
 *
 * This design lets any UI (RecruitingModal, Join Teams, etc.) read consistently
 * and makes it trivial to add new fields later via a single normalizer.
 */

const API_BATCH_ENDPOINT = '/api/teams/users/by-ids';
const TTL_MS = 10 * 60 * 1000; // 10 minutes

const now = () => Date.now();

const useOtherUsersStore = create(persist((set, get) => ({
  entities: {
    users: {},       // u_id -> user
    businesses: {},  // b_id -> business
  },
  ratings: {},       // u_id -> { avg, count }
  fetchedAt: {},     // u_id -> timestamp (for TTL)

  // ---------- Selectors ----------
  getUser(u_id) {
    const { entities, ratings } = get();
    const u = entities.users?.[Number(u_id)];
    if (!u) return null;

    const b = u.b_id ? entities.businesses?.[Number(u.b_id)] : null;
    const r = ratings?.[Number(u_id)] || null;

    return {
      ...u,

      // Aliases so CandidateListModal can read u_first_name / u_last_name / u_profile_picture
      u_id: u.u_id,
      u_first_name: u.first_name ?? u.u_first_name ?? '',
      u_last_name: u.last_name ?? u.u_last_name ?? '',
      u_profile_picture: u.avatar ?? u.u_profile_picture ?? '',

      // Flatten key business fields so CandidateListModal can read them directly
      b_id: u.b_id ?? b?.b_id ?? null,
      b_name: b?.b_name ?? u.b_name ?? '',
      b_industry: b?.b_business_industry ?? u.b_business_industry ?? b?.b_industry ?? u.b_industry ?? '',
      b_business_industry: b?.b_business_industry ?? u.b_business_industry ?? b?.b_industry ?? u.b_industry ?? '',

      // NEW: flatten the fields CandidateListModal is currently missing
      b_phone: b?.b_phone ?? u.b_phone ?? '',
      b_website: b?.b_website ?? u.b_website ?? '',
      b_logo: b?.b_logo ?? u.b_logo ?? '',
      b_address: b?.b_address ?? u.b_address ?? '',
      b_city: b?.b_city ?? u.b_city ?? '',
      b_state: b?.b_state ?? u.b_state ?? '',
      b_zip_code: b?.b_zip_code ?? u.b_zip_code ?? '',
      b_about_us: b?.b_about_us ?? u.b_about_us ?? '',
      u_email: u.u_email ?? '',
      u_job_title: u.u_job_title ?? '',
      u_phone_number: u.u_phone_number ?? '',
      u_url_name: u.u_url_name ?? '',
      u_customer_focus: u.u_customer_focus ?? '',

      // Flatten rating fields (CandidateListModal uses u_* keys)
      u_avg_rating: r?.avg ?? null,
      u_review_count: r?.count ?? 0,

      // Keep nested objects for other consumers
      business: b || null,
      rating: r || null,

      // Convenience: display fields
      displayName:
        u.full_name ||
        [u.first_name, u.last_name].filter(Boolean).join(' ').trim() ||
        `User #${u.u_id}`,
      avatar: u.avatar || '/images/default-avatar.png',
    };


  },

  // Returns a lightweight array of user view models (convenience for lists)
  getUsers(u_ids = []) {
    return u_ids.map((id) => get().getUser(id)).filter(Boolean);
  },

  // ---------- Upserts ----------
  upsertUsers(usersArr = []) {
    if (!Array.isArray(usersArr) || usersArr.length === 0) return;

    set((state) => {
      const users = { ...state.entities.users };
      const businesses = { ...state.entities.businesses };
      const ratings = { ...state.ratings };
      const fetchedAt = { ...state.fetchedAt };


      for (const raw of usersArr) {
        const norm = normalizeUser(raw);

        // user
        if (norm.u_id != null) {
          users[norm.u_id] = {
            ...(users[norm.u_id] || {}),
            ...norm.user, // keep user-only fields
          };
          fetchedAt[norm.u_id] = now();
        }

        // business
        if (norm.business && norm.business.b_id != null) {
          const bId = norm.business.b_id;
          businesses[bId] = {
            ...(businesses[bId] || {}),
            ...norm.business,
          };
        }

        // rating
        if (norm.rating && norm.u_id != null) {
          ratings[norm.u_id] = {
            avg: norm.rating.avg ?? null,
            count: norm.rating.count ?? 0,
          };
        }
      }

      return {
        entities: { users, businesses },
        ratings,
        fetchedAt,
      };
    });
  },

  // Seed from an existing array (e.g., TeamInfo's nearbyUsers response)
  seedFromArray(arr = []) {
    if (!Array.isArray(arr) || arr.length === 0) return;
    get().upsertUsers(arr);
  },

  // ---------- Seed from partial data ----------
  seedFromPartial: (partialUser) => {
    if (!partialUser || typeof partialUser !== 'object') return;
    
    const u_id = toNum(partialUser.u_id ?? partialUser.peerUid);
    if (!Number.isFinite(u_id) || u_id <= 0) return;
    
    set((state) => {
      const users = { ...state.entities.users };
      const fetchedAt = { ...state.fetchedAt };
      
      // Only update if we don't already have this user or if we're adding missing fields
      const existing = users[u_id] || {};
      
      users[u_id] = {
        ...existing,
        u_id,
        first_name: partialUser.first_name ?? existing.first_name ?? '',
        last_name: partialUser.last_name ?? existing.last_name ?? '',
        full_name: partialUser.full_name ?? 
                   partialUser.displayName ?? 
                   existing.full_name ?? '',
        avatar: partialUser.avatar ?? 
                partialUser.avatarUrl ?? 
                partialUser.profilePhotoUrl ?? 
                partialUser.photoUrl ?? 
                existing.avatar ?? '',
        b_id: existing.b_id ?? null, // Keep existing business ID if any
      };
      
      fetchedAt[u_id] = now();
      
      return {
        entities: { ...state.entities, users },
        fetchedAt,
      };
    });
  },
  // ---------- Fetch ----------
  // Ensure we have fresh profiles for a set of u_ids (uses TTL + batching)
  async fetchByIds(u_ids = []) {
    const ids = (Array.isArray(u_ids) ? u_ids : [u_ids])
      .map((x) => Number(x))
      .filter((x) => Number.isFinite(x));

    if (ids.length === 0) return;

    const { fetchedAt } = get();
    const missing = [];
    const expired = [];

    for (const id of ids) {
      const ts = fetchedAt[id];
      if (!ts) {
        missing.push(id);
      } else if (now() - ts > TTL_MS) {
        expired.push(id);
      }
    }

    const need = [...new Set([...missing, ...expired])];
    if (need.length === 0) return;

    // Batch call (Step 2 will add this endpoint)
    const qs = encodeURIComponent(need.join(','));
    const res = await fetch(`${API_BATCH_ENDPOINT}?ids=${qs}`, { credentials: 'include' });
    if (!res.ok) return;

    const payload = await res.json(); // expect: { users: [...] }
    if (payload && Array.isArray(payload.users)) {
      get().upsertUsers(payload.users);
    }
  },
}), {
  name: 'eh_other_users_v1',
  storage: createJSONStorage(() => localStorage),
  partialize: (state) => ({
    entities: state.entities,
    ratings: state.ratings,
    fetchedAt: state.fetchedAt,
  }),
}));


export default useOtherUsersStore;

/** ---------------- Normalizer ----------------
 * Accepts any shape and returns a stable normalized bundle.
 * Update this in the future when you add more fields.
 */
function normalizeUser(raw = {}) {
  // Accepted inputs (examples):
  // - /profile/users/by-ids response item
  // - TeamInfo "nearbyUsers" item
  // We try to map all common field names to a single shape.

const u_id = toNum(raw.u_id ?? raw.user_id);
const first = raw.u_first_name ?? raw.first_name ?? raw.firstName ?? '';
const last = raw.u_last_name ?? raw.last_name ?? raw.lastName ?? '';
const full = (raw.full_name || [first, last].filter(Boolean).join(' ').trim()) || '';
const avatar =
  raw.u_profile_picture ||
  raw.profile_image_url ||
  raw.profile_picture ||
  raw.avatar ||
  '';

  // NEW: user fields CandidateListModal expects
  const u_email = raw.u_email ?? raw.email ?? '';
  const u_job_title = raw.u_job_title ?? raw.job_title ?? raw.title ?? raw.u_title ?? raw.u_role ?? '';
  const u_url_name = raw.u_url_name ?? raw.url_name ?? '';
  const u_phone_number = raw.u_phone_number ?? raw.phone_number ?? raw.u_phone ?? '';

  const b_id = toNum(raw.b_id ?? raw.business_id);
  const b_name = raw.b_name ?? raw.business_name ?? raw.companyName ?? '';
  const b_business_industry = raw.b_business_industry ?? '';

  // Business phone
  const b_phone = raw.b_phone ?? '';

  // Business website (external link)
  const b_website = raw.b_website ?? raw.website ?? '';

  // Business address fields
  const b_logo = raw.b_logo ?? '';
  const b_address = raw.b_address ?? '';
  const b_city = raw.b_city ?? '';
  const b_state = raw.b_state ?? '';
  const b_zip_code = raw.b_zip_code ?? raw.b_zip ?? '';
  const b_about_us = raw.b_about_us ?? '';

  // User customer focus
  const u_customer_focus = raw.u_customer_focus ?? '';




  const rating_avg = toNum(raw.rating_avg ?? raw.customersurvey_avg ?? raw.avg_rating);
  const rating_count = toNum(raw.rating_count ?? raw.customersurvey_count ?? raw.review_count);

  return {
    u_id: u_id ?? null,
    user: {
      u_id: u_id ?? null,
      first_name: first,
      last_name: last,
      full_name: full,
      avatar: avatar,
      b_id: b_id ?? null,

      // Schema-canonical user fields
      u_email,
      u_job_title,
      u_url_name,
      u_phone_number,
      u_customer_focus,

      // Store b_business_industry directly on user for easy access
      b_business_industry,
    },

    business:
      b_id != null
        ? {
            b_id,
            b_name,
            b_business_industry,
            b_phone,
            b_website,
            b_logo,
            b_address,
            b_city,
            b_state,
            b_zip_code,
            b_about_us,
          }
        : null,

    rating:
      u_id != null
        ? {
            avg: Number.isFinite(rating_avg) ? Number(rating_avg) : null,
            count: Number.isFinite(rating_count) ? Number(rating_count) : 0,
          }
        : null,
  };
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
